// frontend/src/pages/BulkAnalysisPage/ConfigurationModal.jsx

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Box, Typography, Button, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const ConfigurationModal = ({ isOpen, onClose, currentMode, onSave, isSaving }) => {
    const { t } = useTranslation();
    const [selectedMode, setSelectedMode] = useState(currentMode || 'manual');

    useEffect(() => {
        // Reset the selected mode to the current active mode whenever the dialog opens
        if (isOpen) {
            setSelectedMode(currentMode || 'manual');
        }
    }, [currentMode, isOpen]);

    const handleSave = () => {
        onSave(selectedMode);
    };

    // A reusable component for rendering each mode option card
    const ModeOption = ({ mode, title, description, iconSrc }) => (
        <Box
            className={`config-method-box ${selectedMode === mode ? 'selected' : ''} ${isSaving ? 'disabled' : ''}`}
            onClick={() => {
                if (isSaving) return; // Do nothing if a save is in progress

                // --- THIS IS THE KEY LOGIC CHANGE ---
                // If the user clicks on the mode that is already selected,
                // treat it as a confirmation and close the modal.
                if (selectedMode === mode) {
                    onClose();
                } else {
                // Otherwise, just update the selection to the new mode.
                    setSelectedMode(mode);
                }
            }}
        >
            <Box display="flex" alignItems="center" gap={2}>
                <Box className="config-icon-wrapper">
                    <img 
                        src={iconSrc} 
                        alt={`${title} Mode`} 
                    />
                </Box>
                <Box>
                    <Typography variant="subtitle1" fontWeight="bold">{title}</Typography>
                    <Typography variant="body2">{description}</Typography>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                // Reuse the deploy modal's class for the base dark/glass style
                className: "deploy-modal-dialog"
            }}
        >
            <DialogTitle>
                {t('bulkAnalysisPage.configModal.title')}
                <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.500' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Typography gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    {t('bulkAnalysisPage.configModal.description')}
                </Typography>
                
                <ModeOption
                    mode="manual"
                    title={t('bulkAnalysisPage.configModal.modeManual')}
                    description={t('bulkAnalysisPage.configModal.modeManualDesc')}
                    iconSrc="/static/previews/platform/vetra_ai.png"
                />
                
                <ModeOption
                    mode="dcm"
                    title={t('bulkAnalysisPage.configModal.modeDCM')}
                    description={t('bulkAnalysisPage.configModal.modeDCMDesc')}
                    iconSrc="/static/previews/platform/dcm.png"
                />

            </DialogContent>
            <DialogActions sx={{ padding: '16px 24px' }}>
                <Button 
                    onClick={onClose}
                    disabled={isSaving}
                    sx={{ color: 'rgba(255, 255, 255, 0.7)', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' } }}
                >
                    {t('bulkAnalysisPage.dialogs.common.cancel')}
                </Button>
                <Button 
                    onClick={handleSave} 
                    variant="contained" 
                    color="secondary"
                    // Disable save button if nothing has changed
                    disabled={isSaving || selectedMode === currentMode}
                >
                    {isSaving ? <CircularProgress size={24} color="inherit" /> : t('bulkAnalysisPage.tableHeader.saveButton')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfigurationModal;